import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { contentApi } from '../api/contentApi'
import type { ContentType } from '../api/contentApi'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Filter = 'all' | 'missing' | 'covered'

export function useContentTypes() {
  return useQuery({ queryKey: ['content-types'], queryFn: () => contentApi.getContentTypes() })
}

export function useContentSummary(type: ContentType) {
  return useQuery({ queryKey: ['content-summary', type], queryFn: () => contentApi.getContentSummary(type), refetchInterval: 30000 })
}

export function useContentTargets(type: ContentType, filter: Filter) {
  return useInfiniteQuery({
    queryKey: ['content-targets', type, filter],
    queryFn: ({ pageParam }) => contentApi.getContentTargets(type, { filter, cursor: (pageParam as string | null) ?? null }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.pageInfo.hasNextPage ? last.pageInfo.cursor : undefined),
  })
}

export function useGenerateContent() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ type, target_ref }: { type: ContentType; target_ref: string }) => contentApi.generateContent(type, target_ref),
    onSuccess: (r: any) => {
      toast({ title: r.published ? `Published ${r.target_ref}` : r.drafted ? `Drafted ${r.target_ref} (review)` : r.rejected ? `Rejected (quality gate)` : 'Done' })
      qc.invalidateQueries({ queryKey: ['content-targets'] })
    },
    onError: (e: any) => toast({ title: 'Generate failed', description: e?.response?.data?.error || e.message, variant: 'destructive' }),
  })
}

export function useEnqueueContent() {
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ type, handles }: { type: ContentType; handles: string[] }) => contentApi.enqueueContent(type, handles),
    onSuccess: (r) => toast({ title: `Enqueued ${r.enqueued}${r.failed ? ` · ${r.failed} failed` : ''}`, description: 'Generating in the background.' }),
    onError: (e: any) => toast({ title: 'Enqueue failed', description: e?.response?.data?.error || e.message, variant: 'destructive' }),
  })
}
